/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce, getAsset, getUrlSync } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { format, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';
import { _mergeDuplicateCartlines } from '../../../../shared-utils/cart-utils';

/**
 * ICartIconData: Interface for Cart Icon Data.
 */
export interface ICartIconData {
    cart?: ICartState;
}

/**
 * ICartIconComponentProps: Interface for Cart Icon component props.
 */
export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
    cartTooltipLabel: string;
    cartQtyLabel: string;
    telemetryContent?: ITelemetryContent;
    salesAgreementPricePrompt?: string;
    price?: string;
}

/**
 * ICartIconComponent: Interface for Cart Icon component.
 */
export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {}

const CartIconComponentActions = {};

/**
 *
 * CartIcon component.
 * @extends {React.FC<ICartIconComponentProps>}
 */
const CartIcon: React.FC<ICartIconComponentProps> = (props: ICartIconComponentProps) => {
    const {
        cartLabel,
        cartTooltipLabel,
        cartQtyLabel,
        data: { cart },
        price
    } = props;

    const cartItemsCount = React.useMemo(() => {
        return cart?.cart.CartLines ? _mergeDuplicateCartlines(cart.cart.CartLines).length : 0;
    }, [cart?.cart.CartLines]);
    const qtyLabel = format(cartQtyLabel, cartItemsCount);
    const label = format(cartLabel, cartItemsCount);
    const tooltip = format(cartTooltipLabel, cartItemsCount);

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const style: React.CSSProperties = {
        visibility: cart ? 'visible' : 'hidden'
    };
    return (
        <a
            className={classnames('msc-cart-icon', props.className)}
            href={getUrlSync('cart', props.context.actionContext)}
            role='link'
            aria-label={tooltip}
            title={tooltip}
            {...attributes}
        >
            <img src={getAsset('icons/cart.svg', props.context.request)} />
            <span>{label}</span>

            {price && <span className='msc-cart-icon__price'>{price}</span>}

            <div className='msc-cart-icon__count' aria-hidden style={style}>
                {qtyLabel}
            </div>
        </a>
    );
};

export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error -- Compatible issue with the component override.
    ICartIconComponent
>('CartIcon', { component: CartIcon, ...CartIconComponentActions });

export default CartIconComponent;
